import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Icons as I } from '../pack-icons';
import { BrandIcons, BrandTints } from '../pack-icons/brands';
import { useTranslation } from 'react-i18next';
import { MetricValue } from '../metrics/MetricValue.jsx';
import { engagementRate, reactionField } from '../../lib/socialMetrics.js';
import VideoFramePreview from '../VideoFramePreview.jsx';
export { formatMetric } from '../../lib/socialMetrics.js';

/**
 * Format a numeric metric for compact display.
 *  1234567 → "1.2M"
 *    14823 → "15k"
 *      842 → "842"
 */

const STATUS_META = {
  published: { label: 'Published', cls: 'pill-success' },
  scheduled: { label: 'Scheduled', cls: 'pill-info' },
  planned: { label: 'Planned', cls: 'pill-info' },
  ready: { label: 'Ready', cls: 'pill-info' },
  publishing: { label: 'Publishing', cls: 'pill-info' },
  draft: { label: 'Draft', cls: '' },
  // TASK-074 — text ready, visual failed, no media: waiting on the user's
  // image or an explicit text-only opt-in. Label is localized below.
  held: { label: 'Waiting for image', cls: 'pill-warning' },
  failed: { label: 'Failed', cls: 'pill-warning' },
  attention: { label: 'Needs attention', cls: 'pill-warning' },
  inbox: { label: '', cls: 'pill-inbox' },
  needs_check: { label: '', cls: 'pill-warning' },
  generating: { label: 'Generating', cls: 'pill-info' },
  // TASK-331 (M7 fix): neutral styling — same empty class as 'draft' (base
  // `.pill` look, no success/info/warning tint). A lapsed org's paused post,
  // not a failure the user needs to act on beyond renewing.
  blocked_billing: { label: 'Paused (plan lapsed)', cls: '' },
  // (round 4: the round-3 'missed' entry is gone — a missed-while-lapsed
  // post now maps to the plain 'failed' entry above, same as any failure.)
};

/**
 * LibraryCard — pack-aligned post card.
 *
 * Props:
 *   - id, platform, status, title, snippet, date (display string), media ('image'|'carousel'|'video'|'thread'),
 *     metrics ({ views, likes, comments, shares, engagement }), serif (italic title styling)
 *   - onEdit, onDuplicate, onPreview, onMore, onAction (status-specific CTA)
 *
 * Hover quick-action buttons stop propagation so they do not navigate via the cover Link.
 */
export default function LibraryCard({
  id,
  platform = 'instagram',
  status = 'draft',
  title = '',
  snippet = '',
  date = '',
  media,
  metrics,
  imageUrl,
  videoUrl,
  brand,
  serif = false,
  href,
  onEdit,
  onDuplicate,
  onPreview,
  onMore,
  onAction,
  actionLabel,
}) {
  const { t } = useTranslation();
  const [hover, setHover] = useState(false);
  const sm = STATUS_META[status] || STATUS_META.draft;
  const statusLabel = status === 'inbox' ? t('postPreview.inboxDraftTitle')
    : status === 'needs_check' ? t('postPreview.statusNeedsCheck')
    : status === 'held' ? t('library.heldBadge')
    : sm.label;
  const tint = BrandTints[platform] || BrandTints.x;
  const gradient = `linear-gradient(135deg, ${tint.from} 0%, ${tint.to} 100%)`;
  const coverStyle = imageUrl
    ? { background: `linear-gradient(180deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.55) 100%), url("${imageUrl}") center/cover no-repeat` }
    : { background: gradient };
  const coverHref = href || (id != null ? `/app/post/${id}` : '#');
  const isPublished = status === 'published';
  const hasMetrics = isPublished || (metrics && (
    metrics.views > 0
    || metrics.likes > 0
    || metrics.comments > 0
    || metrics.shares > 0
  ));
  const engagement = engagementRate(metrics);

  return (
    <article
      className={`lib-card lib-card-${status}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <Link to={coverHref} className="lib-cover" style={coverStyle}>
        {videoUrl && <VideoFramePreview url={videoUrl} />}
        <span className={`lib-plat-ic ob-net-ic-${platform}`}>
          {BrandIcons[platform]?.({ size: 18 })}
        </span>

        <span className={`pill ${sm.cls} lib-status`}>
          <span className="dot" />
          {statusLabel}
        </span>

        {media && (
          <span className="lib-media mono">
            {media === 'video' && (<><I.play size={10} /> Video</>)}
            {media === 'carousel' && (<><I.copy size={9} /> Carousel</>)}
            {media === 'thread' && 'Thread'}
            {media === 'image' && 'Image'}
          </span>
        )}

        <div
          className="lib-cover-text"
          style={serif ? { fontFamily: 'var(--font-serif)', fontStyle: 'italic' } : undefined}
        >
          {title}
        </div>

        {hover && (
          <div
            className="lib-quick"
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
          >
            {onEdit && (<button title="Edit" onClick={onEdit} type="button"><I.edit size={13} /></button>)}
            {onDuplicate && (<button title="Duplicate" onClick={onDuplicate} type="button"><I.copy size={13} /></button>)}
            {onPreview && (<button title="Preview" onClick={onPreview} type="button"><I.eye size={13} /></button>)}
            {onMore && (<button title="More" onClick={onMore} type="button"><I.dots size={13} /></button>)}
          </div>
        )}
      </Link>

      <div className="lib-body">
        {snippet && <p className="lib-snippet">{snippet}</p>}

        {hasMetrics ? (
          <div className="lib-metrics">
            <div className="lib-metric">
              <I.eye size={12} />
              <MetricValue metrics={metrics} field="views" compact />
            </div>
            <div className="lib-metric">
              <I.heart size={12} />
              <MetricValue metrics={metrics} field={reactionField(metrics, platform)} compact />
            </div>
            <div className="lib-metric">
              <I.chat size={12} />
              <MetricValue metrics={metrics} field="comments" compact />
            </div>
            <div className="lib-metric">
              <I.share size={12} />
              <MetricValue metrics={metrics} field="shares" compact />
            </div>
            {engagement != null && (
              <div className="lib-metric lib-eng">
                <I.trend size={12} />
                <span className="mono">{engagement.toFixed(1)}%</span>
              </div>
            )}
          </div>
        ) : (
          <div className="lib-no-metrics">
            <I.clock size={11} />
            <span>{t('socialMetrics.afterPublish')}</span>
          </div>
        )}

        <div className="lib-foot">
          <span className="lib-date mono">
            <I.calendar size={11} />
            {date}
          </span>
          {actionLabel && onAction && (
            <button
              className={`btn ${status === 'published' ? 'btn-ghost' : 'btn-soft'} btn-sm`}
              onClick={onAction}
              type="button"
            >
              {actionLabel}
              {status !== 'published' && <I.arrowRight size={11} />}
              {status === 'published' && <I.external size={11} />}
            </button>
          )}
          {brand && (
            <span className="lib-brand-tile" title={brand.name}>
              {brand.logoUrl ? <img src={brand.logoUrl} alt="" /> : (brand.monogram || brand.name?.trim().charAt(0).toUpperCase())}
            </span>
          )}
        </div>
      </div>
    </article>
  );
}
